import React, { useId } from 'react';
import SecretInput from '@/components/ui/SecretInput';
import { ToggleField } from '@/components/ui';

export type SettingsFieldProps = {
  id?: string;
  label: string;
  hint?: React.ReactNode;
  error?: string;
  tooltip?: string;
  type?: 'text' | 'number' | 'password' | 'checkbox' | 'select';
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder?: string;
  disabled?: boolean;
  onClear?: () => void;
  showClear?: boolean;
  options?: { value: string; label: string }[];
  /** Renders SecretInput with show/hide (and optional copy for admins). */
  secret?: boolean;
  allowCopy?: boolean;
};

export default function SettingsField({
  id: idProp,
  label,
  hint,
  error,
  tooltip,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled = false,
  onClear,
  showClear = false,
  options = [],
  secret = false,
  allowCopy = false,
}: SettingsFieldProps) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  if (type === 'checkbox') {
    return (
      <div className={error ? 'settings-field-wrap settings-field-wrap--error' : 'settings-field-wrap'}>
        <ToggleField
          id={inputId}
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(next) => onChange(next)}
          label={label}
          hint={hint}
          aria-label={typeof label === 'string' ? label : undefined}
        />
        {error ? (
          <p className="settings-field__error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className={`settings-field ${error ? 'settings-field--error' : ''}`}>
        <label className="settings-field__label" htmlFor={inputId}>
          {label}
        </label>
        <select
          id={inputId}
          className="settings-input settings-input--select"
          value={value != null ? String(value) : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {hint && (
          <p className="settings-field__hint" id={hintId}>
            {hint}
          </p>
        )}
        {error && (
          <p className="settings-field__error" id={errorId} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`settings-field ${error ? 'settings-field--error' : ''}`}>
      <div className="settings-field__label-row">
        <label className="settings-field__label" htmlFor={inputId}>
          {label}
          {tooltip && (
            <span className="settings-field__tooltip" title={tooltip} aria-label={tooltip}>
              ?
            </span>
          )}
        </label>
        {showClear && onClear && (
          <button type="button" className="settings-field__clear" onClick={onClear}>
            Clear
          </button>
        )}
      </div>
      {secret ? (
        <SecretInput
          id={inputId}
          className="settings-input"
          value={value != null ? String(value) : ''}
          onChange={(v) => onChange(v)}
          placeholder={placeholder}
          disabled={disabled}
          allowCopy={allowCopy}
          aria-invalid={Boolean(error)}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        />
      ) : (
        <input
          id={inputId}
          className="settings-input"
          type={type === 'password' ? 'password' : type === 'number' ? 'number' : 'text'}
          value={value != null ? String(value) : ''}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) =>
            onChange(type === 'number' ? (e.target.value ? Number(e.target.value) : '') : e.target.value)
          }
          aria-invalid={Boolean(error)}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        />
      )}
      {hint && (
        <p className="settings-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="settings-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
